import en from "../assets/locale/en.json";
import ja from "../assets/locale/ja.json";
import es from "../assets/locale/es.json";
import fr from "../assets/locale/fr.json";
import ru from "../assets/locale/ru.json";
import zh from "../assets/locale/zh.json";

const LOCALES = { en, ja, es, fr, ru, zh };
const DEFAULT_LOCALE = "en";
const LOCALE_STORAGE_KEY = "slLocale";
// UI languages the app will auto-select from the device/browser language on
// first load. English, Japanese and Chinese are the enabled languages.
const AUTO_DETECT_LOCALES = new Set(["en", "ja", "zh"]);

const isSupported = (value) => Boolean(value && LOCALES[value]);

const normalizeLocale = (raw) => {
  if (!raw) return null;
  const value = String(raw).toLowerCase();
  if (LOCALES[value]) return value;
  const base = value.split(/[-_]/)[0];
  if (LOCALES[base]) return base;
  if (base === "jp") return "ja";
  if (base === "zh" || value.startsWith("zh")) return "zh";
  return null;
};

const getPreferredLocale = (languages) => {
  const candidates = Array.isArray(languages) ? languages : [languages];
  for (const candidate of candidates) {
    const locale = normalizeLocale(candidate);
    if (AUTO_DETECT_LOCALES.has(locale)) return locale;
  }
  return null;
};

const readDeviceLocale = () => {
  try {
    if (typeof navigator === "undefined") return null;
    const languages = Array.isArray(navigator.languages)
      ? [...navigator.languages, navigator.language]
      : [navigator.language];
    return getPreferredLocale(languages);
  } catch {
    return null;
  }
};

const readStoredLocale = () => {
  try {
    const stored =
      window.sessionStorage.getItem(LOCALE_STORAGE_KEY) ||
      window.localStorage.getItem(LOCALE_STORAGE_KEY);
    return isSupported(stored) ? stored : null;
  } catch {
    return null;
  }
};

const writeStoredLocale = (locale) => {
  try {
    window.sessionStorage.setItem(LOCALE_STORAGE_KEY, locale);
    // WebViews may recreate the browsing session during authentication. Keep
    // the explicit user choice across that reload instead of falling back to
    // the device/default locale.
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // ignore storage failures
  }
};

const applyDocumentLang = (locale) => {
  try {
    if (typeof document !== "undefined") {
      document.documentElement.lang = locale;
    }
  } catch {
    // ignore (SSR / no document)
  }
};

// Resolve the boot locale once: ?locale= URL param wins (and is persisted),
// otherwise a previously stored choice, then the phone/browser preference,
// and finally English. React Native WebView exposes the host device language
// through navigator.languages, so the native shell does not need a separate
// locale bridge just to choose the initial language.
const resolveInitialLocale = () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const fromUrl = normalizeLocale(urlParams.get("locale"));
    if (fromUrl) {
      writeStoredLocale(fromUrl);
      return fromUrl;
    }
  } catch {
    // ignore (no window)
  }
  return readStoredLocale() || readDeviceLocale() || DEFAULT_LOCALE;
};

// --- Reactive locale store -------------------------------------------------
// A single source of truth for the active locale. React subscribes via
// `subscribe`/`getCurrentLocale` (see src/i18n), and `pageLocale`/`translate`
// read the same `currentLocale`, so the UI, the compat shim, and the report
// layer can never diverge.
let currentLocale = resolveInitialLocale();
const listeners = new Set();
applyDocumentLang(currentLocale);

const getCurrentLocale = () => currentLocale;

const setLocale = (raw) => {
  const next = normalizeLocale(raw) || DEFAULT_LOCALE;
  if (next === currentLocale) return currentLocale;
  currentLocale = next;
  writeStoredLocale(next);
  applyDocumentLang(next);
  listeners.forEach((listener) => listener());
  return next;
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

// --- Lookup helpers --------------------------------------------------------
const getByPath = (obj, path) =>
  path
    .split(".")
    .reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);

const interpolate = (value, vars) => {
  if (typeof value !== "string" || !vars) return value;
  return value.replace(/\{(\w+)\}/g, (match, name) =>
    vars[name] != null ? String(vars[name]) : match,
  );
};

// Backwards-compatible section getter — returns the whole section object for a
// page, falling back to the English section. Existing callers destructure keys
// off the result, so this keeps working while components migrate to `useT`.
const pageLocale = (page) => {
  const bundle = LOCALES[currentLocale] || LOCALES[DEFAULT_LOCALE];
  return bundle[page] ?? LOCALES[DEFAULT_LOCALE][page];
};

// Resolve a single key (dot-path, optionally scoped to `section`) for the
// active locale, with English fallback and `{var}` interpolation. A key missing
// in every locale renders "" in production and the key path in dev (with warn),
// so we never surface a blank where English exists.
const translate = (section, key, vars) => {
  const path = section ? `${section}.${key}` : key;
  const primary = getByPath(LOCALES[currentLocale], path);
  const fallback = getByPath(LOCALES[DEFAULT_LOCALE], path);
  let value = primary != null ? primary : fallback;
  if (value == null) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn(`[i18n] missing translation key: ${path}`);
      return path;
    }
    value = "";
  }
  return interpolate(value, vars);
};

export {
  pageLocale,
  getCurrentLocale,
  setLocale,
  subscribe,
  translate,
  normalizeLocale,
  getPreferredLocale,
  DEFAULT_LOCALE,
  LOCALES,
};
